'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  EventClickArea,
  normalizeTrackingValue,
  trackEvent,
} from '@tuic/tracking';
import { useEffect, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { IconButton } from '../../../core-components';
import { Button } from '../../../core-components/Button';
import { PasswordField } from '../../../core-components/PasswordField';
import { replaceTextInNode } from '../../../utils/react-tree';

import type { I18NInterface } from '../../../utils';
import type { ComponentProps, FC, ReactNode } from 'react';

const TRACKING_NAME = 'manifest-pin-dialog';

export type ManifestPinDialogProps = {
  open?: ComponentProps<typeof DialogPrimitive.Root>['open'];
  onOpenChange?: ComponentProps<typeof DialogPrimitive.Root>['onOpenChange'];

  headline?: ReactNode;
  subline?: ReactNode;
  travellerName?: string;

  onPinEnter?: (pin: string) => boolean;
  onPinForgot?: () => void;

  closeable?: boolean;

  i18n?: I18NInterface<
    | 'pinDialogContinue'
    | 'pinDialogPinForgotten'
    | 'pinDialogHeadline'
    | 'pinDialogError'
  >;
};

/**
 * Self-contained manifest PIN dialog — visually modelled after the shared
 * `Dialog` / `DialogContent` but built directly on Radix primitives so its
 * mobile behaviour can deviate from the shared bottom-sheet without affecting
 * any other dialog.
 *
 * Deviations from the shared Dialog:
 *
 * 1. **Top-attached on mobile.** The overlay uses `items-start` (and a
 *    slide-from-top animation) so the dialog drops in from the top of the
 *    viewport. The on-screen keyboard opens at the bottom — having the dialog
 *    anchored at the top means the two never collide.
 * 2. **Auto-focus via `useEffect` + 100 ms.** Programmatic `focus()` on iOS
 *    does NOT auto-open the on-screen keyboard. The input shows a caret;
 *    the user taps it themselves when they want to type. This is the
 *    desired UX.
 *
 * On desktop (`md+`) the dialog looks identical to the shared `Dialog`
 * (centered card with `max-w-[640px]`).
 */
export const ManifestPinDialog: FC<ManifestPinDialogProps> = ({
  open,
  onOpenChange,
  headline,
  subline,
  travellerName,
  onPinEnter,
  // onPinForgot,
  closeable,
  i18n,
}) => {
  const [enteredPin, setEnteredPin] = useState('');
  const [isError, setIsError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset error on type
  useEffect(() => {
    setIsError(false);
  }, [enteredPin]);

  // Deviation #2 — auto-focus 100 ms after open. Programmatic `focus()` on
  // iOS doesn't auto-open the keyboard; the user has to tap the input to
  // bring it up themselves, which is what we want.
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [open]);

  const handleOpenChange = (next: boolean): void => {
    trackEvent({
      event_name: next ? 'layer-open-user' : 'layer-close',
      layer_name: normalizeTrackingValue(TRACKING_NAME),
    });
    onOpenChange?.(next);
  };

  const handlePinSubmit = (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const success = onPinEnter?.(enteredPin) ?? false;
    if (!success) setIsError(true);
  };

  const showCloseButton = closeable !== false;
  const title = i18n?.pinDialogHeadline;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={twJoin(
            'fixed inset-0 z-dialog flex',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'firefox:data-[state=closed]:animate-none firefox:data-[state=open]:animate-none',
            // Deviation #1 — top-attached on mobile, centered on desktop.
            'items-start bg-overlay backdrop-blur-sm md:items-center md:justify-center md:p-8 xl:px-[80px]',
            'data-[state=open]:duration-300 data-[state=closed]:duration-500',
          )}
        >
          <DialogPrimitive.Content
            // Don't let Radix steal the initial focus — the `useEffect` above
            // owns it (and gives the open-animation 100 ms to settle first).
            onOpenAutoFocus={event => event.preventDefault()}
            aria-describedby={undefined}
            data-track-click-area={EventClickArea.Dialog}
            data-track-dialog={normalizeTrackingValue(TRACKING_NAME)}
            className={twJoin(
              'relative flex w-full flex-col overflow-hidden bg-surface-secondary-7',
              // Mobile: drop down from top, rounded at the bottom, 32px
              // breathing room at the (lower) end.
              'max-h-[calc(100%_-_theme(space.8))] rounded-b-md shadow-md',
              // Desktop: centered card with the shared 640px max-width.
              'md:max-h-full md:max-w-[640px] md:rounded-md',
              'duration-200',
              'data-[state=open]:animate-in data-[state=open]:fade-in-0 md:data-[state=open]:zoom-in-95',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 md:data-[state=closed]:zoom-out-95',
              'data-[state=open]:duration-300 data-[state=open]:slide-in-from-top-1/2 md:data-[state=open]:slide-in-from-bottom-0',
              'data-[state=closed]:duration-500 data-[state=closed]:slide-out-to-top-1/2 md:data-[state=closed]:slide-out-to-bottom-0',
            )}
          >
            <div className="relative flex h-12 w-full shrink-0 flex-col items-center justify-center border-b-[1px] border-b-secondary-marine-20 bg-surface-white px-12 py-2 text-center">
              <DialogPrimitive.Title className="relative max-w-full truncate text-nowrap text-lg font-semibold text-marine-high-emphasis">
                {title}
              </DialogPrimitive.Title>
              {showCloseButton && (
                <div className="absolute right-2 top-0 flex h-12 items-center justify-center">
                  <DialogPrimitive.Close asChild>
                    <IconButton
                      iconName="cancel"
                      variant="ghost"
                      size="sm"
                      aria-label={`close-${title}`}
                    />
                  </DialogPrimitive.Close>
                </div>
              )}
            </div>

            <div className="flex w-full flex-1 min-h-0 flex-col gap-8 overflow-y-auto overscroll-contain bg-neutral-white p-6 pb-10 text-marine-high-emphasis">
              <div className="space-y-4">
                <h3 className="headline-lg">
                  {replaceTextInNode(
                    headline,
                    '{traveller}',
                    travellerName || '',
                  )}
                </h3>
                <div className="text-lg">
                  {replaceTextInNode(
                    subline,
                    '{traveller}',
                    travellerName || '',
                  )}
                </div>
              </div>

              <form className="space-y-4" onSubmit={handlePinSubmit}>
                <div className="mx-auto w-[250px]">
                  <PasswordField
                    ref={inputRef}
                    maxLength={4}
                    value={enteredPin}
                    onChange={event => setEnteredPin(event.currentTarget.value)}
                    error={isError ? i18n?.pinDialogError : undefined}
                  />
                </div>

                <div className="flex justify-center gap-6">
                  {/* PIN change is not in scope of the first implementation (OM-2521)
                  <TextButton type="button" onClick={onPinForgot}>
                    {i18n?.pinDialogPinForgotten}
                  </TextButton>
                  */}
                  <Button type="submit">{i18n?.pinDialogContinue}</Button>
                </div>
              </form>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
